/** Consent bootstrap has no imports: storage and logging use it during module loading. */
export type ConsentMode = 'pending' | 'full' | 'cookieless';

declare global {
    interface Window {
        __INBOUNDX_CONSENT_MODE__?: ConsentMode;
    }
}

type ConsentListener = (mode: ConsentMode, previous: ConsentMode) => void;
const listeners = new Set<ConsentListener>();

export function isConsentMode(value: unknown): value is ConsentMode {
    return value === 'full' || value === 'pending' || value === 'cookieless';
}

/** Resolve loader attributes before imported modules can touch browser persistence. */
export function getConsentMode(): ConsentMode {
    if (typeof window === 'undefined') return 'full';
    if (window.__INBOUNDX_CONSENT_MODE__ !== undefined) {
        return isConsentMode(window.__INBOUNDX_CONSENT_MODE__)
            ? window.__INBOUNDX_CONSENT_MODE__
            : 'pending';
    }
    if (typeof document !== 'undefined') {
        for (const script of document.querySelectorAll(
            'script[data-consent-mode], script[data-config]'
        )) {
            const attribute = script.getAttribute('data-consent-mode');
            if (attribute !== null) return isConsentMode(attribute) ? attribute : 'pending';
            try {
                const config: unknown = JSON.parse(script.getAttribute('data-config') || '{}');
                if (config && typeof config === 'object' && 'consentMode' in config) {
                    return isConsentMode(config.consentMode) ? config.consentMode : 'pending';
                }
            } catch {
                // The loader validates its config; unrelated script config is not ours.
            }
        }
    }
    return 'full';
}

/** Update synchronously so asynchronous provider callbacks observe withdrawal immediately. */
export function setConsentMode(mode: ConsentMode): void {
    if (!isConsentMode(mode)) throw new TypeError('Invalid Rose consent mode');
    if (typeof window === 'undefined') return;
    const previous = getConsentMode();
    window.__INBOUNDX_CONSENT_MODE__ = mode;
    if (previous === mode) return;
    for (const listener of listeners) {
        // Optional-provider failures must not prevent other cleanup or the reload.
        try {
            listener(mode, previous);
        } catch {
            /* Fail closed using the updated mode. */
        }
    }
}

export function subscribeConsentMode(listener: ConsentListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
